// Render plugin for `chart_rendered` SSE events.
// Registered in AgentChatPageV2 config so the agentkit core stays generic.
// Each event carries { id, platinumData, chartType, title, toolCallId }.
// Charts use the same PlatinumData format as tables; InlinePlatinumTable renders
// in graph mode when passed a defaultViewMode of 'graph'.

import React from 'react'
import type { RenderPlugin, PluginAgentSSEEvent } from '../agent'
import { pluginEventId } from '../agent'
import InlinePlatinumTable from '../components/agent/InlinePlatinumTable'

interface ChartEntry {
  id: string
  platinumData: unknown
  chartType: string
  title: string
  customer?: string
  job?: string
  spec?: string
}

type ChartPluginState = Map<string, ChartEntry>

export const carbonChartPlugin: RenderPlugin<ChartPluginState> = {
  eventTypes: ['chart_rendered'],

  init(): ChartPluginState {
    return new Map()
  },

  reduce(state: ChartPluginState, event: PluginAgentSSEEvent): ChartPluginState {
    const d = event.data
    // The sandbox `chart_rendered` marker event carries `toolCallId` but not an
    // explicit `id` (same shape as table_rendered — see carbonTablePlugin).
    // Several charts can share one toolCallId, so a bare toolCallId fallback
    // would keep only the last chart — suffix with the fold position instead
    // (the fold over pluginEvents is deterministic, so replay derives the same keys).
    const id = pluginEventId(d, state.size)
    if (!id) return state
    const next = new Map(state)
    next.set(id, {
      id,
      platinumData: d.platinumData,
      chartType: (d.chartType as string) || 'bar',
      title: (d.title as string) || '',
      customer: d.customer as string | undefined,
      job: d.job as string | undefined,
      spec: d.spec as string | undefined,
    })
    return next
  },

  render({ state, sessionId }: { state: ChartPluginState; toolCallId: string; sessionId: string }) {
    if (state.size === 0) return null
    const charts = Array.from(state.values())
    return (
      <>
        {charts.map(chart => (
          <InlinePlatinumTable
            key={chart.id}
            platinumData={chart.platinumData}
            title={chart.title || undefined}
            customer={chart.customer}
            job={chart.job}
            spec={chart.spec}
            sessionId={sessionId}
            notebookCellId={chart.id}
            defaultViewMode="graph"
            defaultChartType={chart.chartType}
          />
        ))}
      </>
    )
  },
}
